import { NgModule } from "@angular/core";
import { Routes, RouterModule } from "@angular/router";
import { DashboardComponent } from "./dashboard.component";
import { PermissionGuard } from "@core/guards/permission.guard";
import { USER_PERMISSIONS } from "@core/entities";

const routes: Routes = [
  {
    path: "",
    component: DashboardComponent,
    children: [
      {
        path: "home",
        loadChildren: () =>
          import("@app/modules/home/home.module").then((m) => m.HomeModule),
        canActivate: [PermissionGuard],
        data: {
          roles: [],
        },
      },
      {
        path: "fake-orders",
        loadChildren: () =>
          import("@app/modules/orders/fake-orders/fake-orders.module").then(
            (m) => m.FakeOrdersModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.FAKEORDER_ACCESS],
        },
      },
      {
        path: "users",
        loadChildren: () =>
          import("@app/modules/users/users.module").then((m) => m.UsersModule),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.EDIT_USER],
        },
      },
      {
        path: "order-tracking",
        loadChildren: () =>
          import(
            "@app/modules/orders/order-tracking/order-tracking.module"
          ).then((m) => m.OrderTrackingModule),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.ORDER_TRACKING_ACCESS],
        },
      },
      {
        path: "realtime-fo",
        loadChildren: () =>
          import("@app/modules/orders/realtime-fo/realtime-fo.module").then(
            (m) => m.RealtimeFoModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.REALTIME_FAKE_ORDERS_ACCESS],
        },
      },
      {
        path: "control-panel",
        loadChildren: () =>
          import("@app/modules/orders/control-panel/control-panel.module").then(
            (m) => m.ControlPanelModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.CONTROL_PANEL_ACCESS],
        },
      },
      {
        path: "accuracy-tracker",
        loadChildren: () =>
          import(
            "@app/modules/orders/accuracy-tracker/accuracy-tracker.module"
          ).then((m) => m.AccuracyTrackerModule),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.ACCURACY_TRACKER_ACCESS],
        },
      },
      {
        path: "cargo-invoice",
        loadChildren: () =>
          import("@app/modules/orders/turbo-utils/turbo-utils.module").then(
            (m) => m.TurboUtilsModule
          ),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.CARGO_INVOICE] },
      },
      {
        path: "bulk-upload",
        loadChildren: () =>
          import("@app/modules/bulk-upload/bulk-upload.module").then(
            (m) => m.BulkUploadModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.BULK_UPLOAD_ACCESS],
        },
      },
      {
        path: "charm-price",
        loadChildren: () =>
          import("@app/modules/charm-price/charm-price.module").then(
            (m) => m.CharmPriceModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.CHARM_PRICE_ACCESS],
        },
      },
      {
        path: "sell-pricing",
        loadChildren: () =>
          import("@app/modules/sell-pricing/sell-price.module").then(
            (m) => m.SellPriceModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.SELL_PRICES_ACCESS],
        },
      },
      {
        path: "restrict-list",
        loadChildren: () =>
          import("@app/modules/sell-pricing/sell-price.module").then(
            (m) => m.SellPriceModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.RESTRICT_LIST_ACCESS],
        },
      },
      {
        path: "promotion",
        loadChildren: () =>
          import("@app/modules/promotion/promotion.module").then(
            (m) => m.PromotionModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.PROMOTION_ACCESS],
        },
      },
      {
        path: "promotions-v2",
        loadChildren: () =>
          import("@app/modules/promotions-v2/promotions-v2.module").then(
            (m) => m.PromotionsV2Module
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.PROMOTION_ACCESS],
        },
      },
      {
        path: "markup",
        loadChildren: () =>
          import("@app/modules/markups/markups.module").then(
            (m) => m.MarkupModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.MARKUP_ACCESS],
        },
      },
      {
        path: "rotation-mgr",
        loadChildren: () =>
          import("@app/modules/rotation-manager/rotation-manager.module").then(
            (m) => m.RotationManagerModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.ROTATION_MANAGER_ACCESS],
        },
      },
      {
        path: "complex-discount",
        loadChildren: () =>
          import("@app/modules/promotion/promotion.module").then(
            (m) => m.PromotionModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.COMPLEX_DISCOUNT_ACCESS],
        },
      },
      {
        path: "competitive-price",
        loadChildren: () =>
          import("@app/modules/competitive/competitive.module").then(
            (m) => m.CompetitiveModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.COMPETITIVE_PRICE_ACCESS],
        },
      },
      {
        path: "wishlist",
        loadChildren: () =>
          import("@app/modules/wishlist/wishlist.module").then(
            (m) => m.WishlistModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.WISHLIST_ACCESS],
        },
      },
      {
        path: "packs",
        loadChildren: () =>
          import("@app/modules/packs/packs.module").then((m) => m.PacksModule),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.PACKS_ACCESS],
        },
      },

      {
        path: "label-manager",
        loadChildren: () =>
          import("@app/modules/label_manager/label_manager.module").then(
            (m) => m.LabelManagerModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.LABEL_ACCESS],
        },
      },

      {
        path: "price-manager",
        loadChildren: () =>
          import("@app/modules/price-manager/price-manager.module").then(
            (m) => m.PriceManagerModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.PRICE_MANAGER_ACCESS],
        },
      },
      {
        path: "pricing",
        loadChildren: () =>
          import("@app/modules/pricing/pricing.module").then(
            (m) => m.PricingModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.PRICING_ACCESS],
        },
      },
      {
        path: "purchase-orders",
        loadChildren: () =>
          import(
            "@app/modules/vendors/purchase-orders/purchase-orders.module"
          ).then((m) => m.PurchaseOrdersModule),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.PURCHASE_ORDERS_ACCESS],
        },
      },
      {
        path: "consignment-orders",
        loadChildren: () =>
          import(
            "@app/modules/vendors/consignment-orders/consignment-orders.module"
          ).then((m) => m.ConsignmentOrdersModule),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.CONSIGMENT_ORDERS_ACCESS],
        },
      },
      {
        path: "portals-approval",
        loadChildren: () =>
          import(
            "@app/modules/vendors/approval-request/approval-request.module"
          ).then((m) => m.QueryInvoicesModule),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.REPROCESS_SALE_ORDERS],
        },
      },
      {
        path: "replenishment",
        loadChildren: () =>
          import(
            "@app/modules/supply/planning/replenishment/replenishment.module"
          ).then((m) => m.ReplenishmentModule),
        canActivate: [PermissionGuard],
        data: {
          roles: [
            USER_PERMISSIONS.SUPPLY_PLANNING,
            ,
            USER_PERMISSIONS.REPLENISHMENT_ACCESS,
          ],
        },
      },
      {
        path: "pos",
        loadChildren: () =>
          import("@app/modules/orders/pos/pos.module").then((m) => m.PosModule),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.POS_ACCESS],
        },
      },
      {
        path: "financial-management",
        loadChildren: () =>
          import(
            "@app/modules/vendors/purchase-orders/purchase-orders.module"
          ).then((m) => m.PurchaseOrdersModule),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.FINANCIAL_ACCESS] },
      },
      {
        path: "audit-report",
        loadChildren: () =>
          import("@app/modules/vendors/audit-report/audit-report.module").then(
            (m) => m.AuditReportModule
          ),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.AUDIT_ACCESS] },
      },
      {
        path: "inventory-savvy",
        loadChildren: () =>
          import(
            "@app/modules/inventory/inventory-savvy/inventory-savvy.module"
          ).then((m) => m.InventorySavvyModule),
        canActivate: [PermissionGuard],
        data: {
          roles: [
            USER_PERMISSIONS.SUPPLY_EXECUTION,
            USER_PERMISSIONS.SUPPLY_INVENTORY_ACCESS,
          ],
        },
      },
      {
        path: "transformations",
        loadChildren: () =>
          import(
            "@app/modules/inventory/transformations/transformations.module"
          ).then((m) => m.TransformationsModule),
        canActivate: [PermissionGuard],
        data: {
          roles: [
            USER_PERMISSIONS.SUPPLY_EXECUTION,
            USER_PERMISSIONS.SUPPLY_TRANSFORMATIONS_ACCESS,
          ],
        },
      },
      {
        path: "supply-purchase-orders",
        loadChildren: () =>
          import(
            "@app/modules/supply/execution/purchase-orders/purchase-orders.module"
          ).then((m) => m.PurchaseOrdersModule),
        canActivate: [PermissionGuard],
        data: {
          roles: [
            USER_PERMISSIONS.SUPPLY_EXECUTION,
            USER_PERMISSIONS.SUPPLY_PURCHASE_ORDER_ACCESS,
          ],
        },
      },
      {
        path: "supply-transfer-orders",
        loadChildren: () =>
          import(
            "@app/modules/supply/execution/transfer-orders/transfer-orders.module"
          ).then((m) => m.TransferOrdersModule),
        canActivate: [PermissionGuard],
        data: {
          roles: [
            USER_PERMISSIONS.SUPPLY_EXECUTION,
            USER_PERMISSIONS.SUPPLY_TRANSFER_ORDERS_ACCESS,
          ],
        },
      },
      {
        path: "supply-consignment-orders",
        loadChildren: () =>
          import(
            "@app/modules/supply/execution/consignment-orders/consignment-orders.module"
          ).then((m) => m.ConsignmentOrdersModule),
        canActivate: [PermissionGuard],
        data: {
          roles: [
            USER_PERMISSIONS.SUPPLY_EXECUTION,
            USER_PERMISSIONS.SUPPLY_TRANSFER_ORDERS_ACCESS,
          ],
        },
      },
      {
        path: "audit-inventory",
        loadChildren: () =>
          import("@app/modules/supply/audit/audit.module").then(
            (m) => m.AuditModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [
            USER_PERMISSIONS.SUPPLY_EXECUTION,
            USER_PERMISSIONS.AUDIT_INVENTORY_ACCESS,
          ],
        },
      },
      {
        path: "campaigns",
        loadChildren: () =>
          import("@app/modules/campaign/campaign.module").then(
            (m) => m.CampaignModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [
            USER_PERMISSIONS.SUPPLY_EXECUTION,
            USER_PERMISSIONS.SUPPLY_INVENTORY_ACCESS,
          ],
        },
      },
      {
        path: "multibrand",
        loadChildren: () =>
          import("@app/modules/multibrand/multibrand.module").then(
            (m) => m.MultibrandModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.MULTIBRAND_ACCESS],
        },
      },
      {
        path: "store",
        loadChildren: () =>
          import("@app/modules/multibrand/multibrand.module").then(
            (m) => m.MultibrandModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [USER_PERMISSIONS.STORE_ACCESS],
        },
      },
      {
        path: "assignments",
        loadChildren: () =>
          import("@app/modules/vendors/assignments/assignments.module").then(
            (m) => m.AssignmentsModule
          ),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.TURBOX_ASSOCIATIONS_ACCESS] },
      },
      {
        path: "cyclics",
        loadChildren: () =>
          import("@app/modules/inventory/cyclics/cyclics.module").then(
            (m) => m.CyclicsModule
          ),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.CYCLICS_ACCESS] },
      },
      {
        path: "catalog",
        loadChildren: () =>
          import("@app/modules/catalog/catalog.module").then(
            (m) => m.CatalogModule
          ),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.CATALOG_ACCESS] },
      },
      {
        path: "warehouse-config",
        loadChildren: () =>
          import("@app/modules/catalog/catalog.module").then(
            (m) => m.CatalogModule
          ),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.WAREHOUSE_CONFIG_ACCESS] },
      },
      {
        path: "cold-products",
        loadChildren: () =>
          import(
            "@app/modules/vendors/cold-products/cold-products.module"
          ).then((m) => m.ColdProductsModule),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.COLD_PRODUCTS_ACCESS] },
      },
      {
        path: "kpis",
        loadChildren: () =>
          import("@app/modules/vendors/kpis/kpis.module").then(
            (m) => m.KpisModule
          ),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.KPI_ACCESS] },
      },
      {
        path: "price-list",
        loadChildren: () =>
          import("@app/modules/vendors/price-list/price-list.module").then(
            (m) => m.PriceListModule
          ),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.PRICE_LIST_ACCESS] },
      },
      {
        path: "catman-price-list",
        loadChildren: () =>
          import(
            "@app/modules/vendors/catman-price-list/catman-price-list.module"
          ).then((m) => m.CatmanPriceListModule),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.CATMAN_PRICE_LIST_ACCESS] },
      },
      {
        path: "order-monitoring",
        loadChildren: () =>
          import(
            "@app/modules/vendors/order-monitoring/order-monitoring.module"
          ).then((m) => m.OrderMonitoringModule),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.ORDER_MONITORING_ACCESS] },
      },
      {
        path: "query-invoices",
        loadChildren: () =>
          import(
            "@app/modules/vendors/query-invoices/query-invoices.module"
          ).then((m) => m.QueryInvoicesModule),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.SALE_INVOICE_ACCESS] },
      },
      {
        path: "categories",
        loadChildren: () =>
          import("@app/modules/vendors/categories/categories.module").then(
            (m) => m.CategoriesModule
          ),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.CATEGORIES_ACCESS] },
      },
      {
        path: "parameters",
        loadChildren: () =>
          import("@app/modules/vendors/parameters/parameters.module").then(
            (m) => m.ParametersModule
          ),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.PARAMETER_CONFIGURATION_ACCESS] },
      },
      {
        path: "onboarding",
        loadChildren: () =>
          import("@app/modules/vendors/onboarding/onboarding.module").then(
            (m) => m.OnboardingModule
          ),
        canActivate: [PermissionGuard],
        data: {
          roles: [
            USER_PERMISSIONS.ONBOARDING_ACCESS,
            USER_PERMISSIONS.CATMAN_ONBOARDING_ACCESS,
            USER_PERMISSIONS.DATA_MASTER_ONBOARDING_ACCESS,
          ],
        },
      },
      {
        path: "self-onboarding",
        loadChildren: () =>
          import(
            "@app/modules/vendors/self-onboarding/self-onboarding.module"
          ).then((m) => m.SelfOnboardingModule),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.SELF_ONBOARDING_ACCESS] },
      },
      {
        path: "picker-schedule",
        loadChildren: () =>
          import("@app/modules/picker-schedule/picker-schedule.module").then(
            (m) => m.PickerScheduleModule
          ),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.PICKER_SCHEDULE_ACCESS] },
      },
      {
        path: "commercial-agreements",
        loadChildren: () =>
          import(
            "@app/modules/vendors/commercial-agreements/commercial-agreements.module"
          ).then((m) => m.CommercialAgreementsModule),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.SUPPLIER_MANAGEMENT_ACCESS] },
      },
      {
        path: "payment-back-margins",
        loadChildren: () =>
          import(
            "@app/modules/vendors/payment-back-margins/payment-back-margins.module"
          ).then((m) => m.PaymentBackMarginsModule),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.PAYMENTS_ACCESS] },
      },
      {
        path: "payment-front-margins",
        loadChildren: () =>
          import(
            "@app/modules/vendors/payment-back-margins/payment-back-margins.module"
          ).then((m) => m.PaymentBackMarginsModule),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.FRONT_MARGINS_ACCESS] },
      },
      {
        path: "supplier-management",
        loadChildren: () =>
          import(
            "@app/modules/vendors/supplier-management/supplier-management.module"
          ).then((m) => m.SupplierManagementModule),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.SUPPLIER_ADMIN_ACCESS] },
      },
      {
        path: "product-visibility",
        loadChildren: () =>
          import(
            "@app/modules/product-visibility/product-visibility.module"
          ).then((m) => m.ProductVisibilityModule),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.PRODUCT_VISIBILITY_ACCESS] },
      },
      {
        path: "supplier-metrics",
        loadChildren: () =>
          import(
            "@app/modules/vendors/supplier-metrics/supplier-metrics.module"
          ).then((m) => m.SupplierMetricsModule),
        canActivate: [PermissionGuard],
        data: { roles: [USER_PERMISSIONS.SUPPLIER_METRICS_ACCESS] },
      },
      {
        path: "",
        redirectTo: "home",
        pathMatch: "prefix",
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class DashboardRoutingModule {}
